/**
 * Shared constants for the Project Journey surface.
 * Used by both ProjectJourney (full page) and JourneySteps (extracted block).
 */
import {
  FolderOpen, FileText, Send, ClipboardCheck, DollarSign,
  Users, Camera, PenTool, Hammer, CheckCircle2,
} from 'lucide-react';

// Map an action_type → icon + colors for step thumbnails.
export const ACTION_TYPE_CONFIG = {
  file: { icon: FolderOpen, color: 'text-blue-500', bg: 'bg-blue-500', label: 'File' },
  document: { icon: FileText, color: 'text-indigo-500', bg: 'bg-indigo-500', label: 'Document' },
  email: { icon: Send, color: 'text-green-500', bg: 'bg-green-500', label: 'Email' },
  survey: { icon: ClipboardCheck, color: 'text-purple-500', bg: 'bg-purple-500', label: 'Survey' },
  invoice: { icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500', label: 'Invoice' },
  meeting: { icon: Users, color: 'text-orange-500', bg: 'bg-orange-500', label: 'Meeting' },
  site_visit: { icon: Camera, color: 'text-rose-500', bg: 'bg-rose-500', label: 'Site Visit' },
  design: { icon: PenTool, color: 'text-pink-500', bg: 'bg-pink-500', label: 'Design' },
  contractor: { icon: Hammer, color: 'text-amber-500', bg: 'bg-amber-500', label: 'Contractor' },
  approval: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-600', label: 'Approval' },
};

// Phase colors for visual distinction (cycled by index).
export const PHASE_COLORS = [
  { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-300 dark:border-blue-700' },
  { bg: 'from-purple-500 to-purple-600', light: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-300 dark:border-purple-700' },
  { bg: 'from-emerald-500 to-emerald-600', light: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-300 dark:border-emerald-700' },
  { bg: 'from-orange-500 to-orange-600', light: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-300 dark:border-orange-700' },
  { bg: 'from-rose-500 to-rose-600', light: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-300 dark:border-rose-700' },
  { bg: 'from-cyan-500 to-cyan-600', light: 'bg-cyan-50 dark:bg-cyan-900/20', border: 'border-cyan-300 dark:border-cyan-700' },
  { bg: 'from-amber-500 to-amber-600', light: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-300 dark:border-amber-700' },
  { bg: 'from-indigo-500 to-indigo-600', light: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-300 dark:border-indigo-700' },
];

// ---- Shared step-status + progress helpers (single source of truth) ----
// LEGACY DATA SHAPE: older projects store a phase's items in tasks[] (often
// without ids) instead of steps[]. Read steps[] when non-empty, else tasks[]
// — exactly like backend ai_chad.py ("steps or tasks or []"). Task items get
// a synthesized stable id 'task-<index>' which the step-update endpoint
// resolves back to the task. No data is migrated.
export const getPhaseItems = (phase) => {
  const steps = phase?.steps || [];
  if (steps.length > 0) return steps;
  return (phase?.tasks || []).map((t, idx) => ({
    status: t.completed ? 'completed' : 'pending',
    ...t,
    id: t.id || `task-${idx}`,
  }));
};

// A step with status 'skipped' is "Not needed": it drops OUT of the progress
// denominator instead of counting against completion.
export const isStepNotNeeded = (step) => step?.status === 'skipped';
export const isStepCompleted = (step) =>
  !isStepNotNeeded(step) && Boolean(step?.completed || step?.status === 'completed');

// Unified journey progress formula (mirrored in backend routes/ai_chad.py):
//   progress = completed / (total - notNeeded)
export const computePhaseProgress = (phase) => {
  const steps = getPhaseItems(phase);
  const total = steps.length;
  const notNeeded = steps.filter(isStepNotNeeded).length;
  const completed = steps.filter(isStepCompleted).length;
  const denom = total - notNeeded;
  const progress = denom > 0 ? Math.round((completed / denom) * 100) : (total > 0 ? 100 : 0);
  return { total, completed, notNeeded, denom, progress };
};

export const computeJourneyProgress = (phases) => {
  let total = 0;
  let completed = 0;
  let notNeeded = 0;
  (phases || []).forEach((phase) => {
    const p = computePhaseProgress(phase);
    total += p.total;
    completed += p.completed;
    notNeeded += p.notNeeded;
  });
  const denom = total - notNeeded;
  const progress = denom > 0 ? Math.round((completed / denom) * 100) : (total > 0 ? 100 : 0);
  return { total, completed, notNeeded, denom, progress };
};
